/**
 * 日付ユーティリティ。**すべてJST基準。**
 * ブラウザのタイムゾーンに依存させないため、Intl で明示的に Asia/Tokyo を指定する。
 */
import type { DateStr, MonthStr } from './types';

const JST = 'Asia/Tokyo';

const jstFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: JST,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/** Date を JST の 'YYYY-MM-DD' に変換する */
export function toDateStr(d: Date): DateStr {
  // en-CA は 'YYYY-MM-DD' 形式で出力される
  return jstFormatter.format(d);
}

/** 今日(JST)の 'YYYY-MM-DD' */
export function todayStr(): DateStr {
  return toDateStr(new Date());
}

/** 'YYYY-MM-DD' → 'YYYY-MM' */
export function toMonth(date: DateStr): MonthStr {
  return date.slice(0, 7);
}

/** 今月(JST)の 'YYYY-MM' */
export function currentMonth(): MonthStr {
  return toMonth(todayStr());
}

/** 'YYYY-MM-DD' が妥当か(存在しない日付も弾く) */
export function isValidDateStr(v: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(v)) return false;
  const [y, m, d] = v.split('-').map(Number) as [number, number, number];
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d;
}

/**
 * 年度を算出する。start_month は settings/fiscal から渡す(暫定4月)。
 * 例) start_month=4 のとき 2026-03-31 は 2025年度、2026-04-01 は 2026年度。
 */
export function fiscalYearOf(date: DateStr, startMonth: number): number {
  const year = Number(date.slice(0, 4));
  const month = Number(date.slice(5, 7));
  return month >= startMonth ? year : year - 1;
}

/** 年度(fiscalYear)に含まれる12か月を 'YYYY-MM' の配列で返す */
export function monthsOfFiscalYear(fiscalYear: number, startMonth: number): MonthStr[] {
  return Array.from({ length: 12 }, (_, i) => {
    const m = ((startMonth - 1 + i) % 12) + 1;
    const y = startMonth + i > 12 ? fiscalYear + 1 : fiscalYear;
    return `${y}-${String(m).padStart(2, '0')}`;
  });
}

/** 日数を加算した 'YYYY-MM-DD' を返す(負数で減算) */
export function addDays(date: DateStr, days: number): DateStr {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  const dt = new Date(Date.UTC(y, m - 1, d + days));
  return dt.toISOString().slice(0, 10);
}

/** 土日を除いた営業日で加算する(祝日は考慮しない。CSV消込の候補日レンジに使う) */
export function addBusinessDays(date: DateStr, days: number): DateStr {
  const step = days >= 0 ? 1 : -1;
  let remaining = Math.abs(days);
  let cur = date;
  while (remaining > 0) {
    cur = addDays(cur, step);
    const [y, m, d] = cur.split('-').map(Number) as [number, number, number];
    const dow = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
    if (dow !== 0 && dow !== 6) remaining--;
  }
  return cur;
}

/** 'YYYY-MM' → その月の末日 'YYYY-MM-DD' */
export function endOfMonth(month: MonthStr): DateStr {
  const [y, m] = month.split('-').map(Number) as [number, number];
  const d = new Date(Date.UTC(y, m, 0));
  return d.toISOString().slice(0, 10);
}

const WEEKDAYS = ['日', '月', '火', '水', '木', '金', '土'] as const;

/** 'YYYY-MM-DD' → '(木)' のような曜日表記(■13・2026-08-28) */
export function weekdayLabel(date: DateStr): string {
  const d = new Date(`${date}T00:00:00`);
  return Number.isNaN(d.getTime()) ? '' : `(${WEEKDAYS[d.getDay()]})`;
}

/** 'YYYY-MM-DD' → 'MM/DD(木)'(一覧表示用。■13で曜日を併記) */
export function toShortDate(date: DateStr): string {
  return date.slice(5).replace('-', '/') + weekdayLabel(date);
}

/** 'YYYY-MM' → '2026年8月' */
export function toMonthLabel(month: MonthStr): string {
  const [y, m] = month.split('-');
  return `${y}年${Number(m)}月`;
}

/** FirestoreのTimestamp(またはnull)を「MM/DD(木)」表示にする。未設定は「—」(■13で曜日併記) */
export function tsToShortDate(ts: unknown): string {
  if (!ts || typeof (ts as { toDate?: unknown }).toDate !== 'function') return '—';
  const d = (ts as { toDate: () => Date }).toDate();
  return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}(${WEEKDAYS[d.getDay()]})`;
}

/** Timestampをソート用のミリ秒にする。未設定は0 */
export function tsToMillis(ts: unknown): number {
  if (!ts || typeof (ts as { toMillis?: unknown }).toMillis !== 'function') return 0;
  return (ts as { toMillis: () => number }).toMillis();
}

/** FirestoreのTimestampを「YYYY/MM/DD」表示にする。未設定は「—」 */
export function tsToDateLabel(ts: unknown): string {
  if (!ts || typeof (ts as { toDate?: unknown }).toDate !== 'function') return '—';
  const d = (ts as { toDate: () => Date }).toDate();
  return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}`;
}
